import Vue from "vue";
import Router from "vue-router";
import Login from "./views/Login";
import ForgotPassword from "./views/ForgotPassword";
import ResetPassword from "./views/ResetPassword";
import NotFound from "./views/NotFound";
import SignUp from "./views/SignUp";
import ServerError from "./views/ServerError";
import Logout from "./views/Logout";
import ResetPasswordInitSuccess from "./views/ResetPasswordInitSuccess";
import ResetPasswordFinishSuccess from "./views/ResetPasswordFinishSuccess";
import BillingBlocker from "./views/BillingBlocker";
import InvitationSetup from "./components/InvitationSetup";
import LinkExpired from "./views/LinkExpired";
import OnBoardingEmailSent from "@/components/OnBoardingEmailSent";
import Support from "@/views/Support";
import SupportForm from "@/views/SupportForm";

// import { apiUrl } from "~/config";
import store from "@/store";

Vue.use(Router);

export const PRIVATE_ROUTES = [
  "campaigns",
  "logout",
  "campaign-details",
  "templates",
  "contacts",
  "contact-details",
  "account-user",
  "account-billing",
  "account-integrations",
  "account-advanced",
  "postcards-templates-settings",
  "history",
  "dashboard-mail",
  "letters",
  "dashboard",
  "sign-up",
  "support",
  "support-form"
];

const router = new Router({
  base: process.env.BASE_URL,
  routes: [
    // redirects
    {
      path: "/account/settings",
      name: "account-settings",
      redirect: "/account/organization/general"
    },

    // not found
    {
      path: "/404",
      name: "404",
      component: NotFound
    },
    {
      path: "/500",
      name: "500",
      component: ServerError
    },
    {
      path: "*",
      redirect: "/404"
    },
    {
      path: "/link-expired",
      name: "link-expired",
      component: LinkExpired
    },

    // service
    {
      path: "/invitation-setup",
      name: "invitation-setup",
      component: InvitationSetup
    },
    {
      path: "/onboarding-email-sent",
      name: "onboarding-email-sent",
      component: OnBoardingEmailSent
    },
    {
      path: "/login",
      name: "login",
      component: Login
    },
    {
      path: "/forgot-password",
      name: "forgot-password",
      component: ForgotPassword
    },
    {
      path: "/forgot-password/success",
      name: "forgot-password-success",
      component: ResetPasswordInitSuccess
    },
    {
      path: "/reset-password",
      name: "reset-password",
      component: ResetPassword
    },
    {
      path: "/reset-password/success",
      name: "reset-password-success",
      component: ResetPasswordFinishSuccess
    },
    {
      path: "/signup",
      name: "sign-up",
      component: SignUp,
      meta: { requiresAuth: true }
    },

    {
      path: "/logout",
      name: "logout",
      component: Logout,
      meta: { requiresAuth: true }
    },

    {
      path: "/payment",
      name: "payment",
      component: BillingBlocker,
      meta: { requiresAuth: true }
    },

    //support
    {
      path: "/support",
      name: "support",
      component: Support
    },
    {
      path: "/support-form",
      name: "support-form",
      component: SupportForm
    },

    // campaigns
    {
      path: "/campaigns",
      name: "campaigns",
      component: () =>
        import(/* webpackChunkName: "campaigns" */ "./views/Campaigns.vue"),
      meta: { requiresAuth: true }
    },
    {
      path: "/campaigns/:id",
      name: "campaign-details",
      component: () =>
        import(
          /* webpackChunkName: "campaign-details" */ "./views/CampaignDetails.vue"
        ),
      meta: { requiresAuth: true }
    },

    // templates
    {
      path: "/templates",
      name: "templates",
      component: () =>
        import(/* webpackChunkName: "templates" */ "./views/Templates.vue"),
      meta: { requiresAuth: true }
    },
    {
      path: "/postcards-templates/settings/:id",
      name: "postcards-templates-settings",
      component: () =>
        import(
          /* webpackChunkName: "builder" */ "./views/PostcardsTemplatesSettings"
        ),
      meta: { requiresAuth: true }
    },
    {
      path: "/postcards-templates/builder/:id",
      name: "postcards-templates-builder",
      component: () =>
        import(/* webpackChunkName: "builder" */ "./views/TemplateBuilder"),
      meta: { requiresAuth: true }
    },
    {
      path: "/templates/__$h-builder/:id&",
      name: "h-builder",
      component: () =>
        import(/* webpackChunkName: "builder" */ "./views/TemplateBuilder"),
      meta: { requiresAuth: true }
    },

    {
      path: "/templates/__$h-builder/:id",
      name: "h-builder",
      component: () =>
        import(/* webpackChunkName: "builder" */ "./views/TemplateBuilder"),
      meta: { requiresAuth: true }
    },

    // contacts
    {
      path: "/contacts",
      name: "contacts",
      component: () =>
        import(/* webpackChunkName: "contacts" */ "./views/Contacts"),
      meta: { requiresAuth: true }
    },
    {
      path: "/contacts/import-history",
      name: "import-history",
      component: () =>
        import(
          /* webpackChunkName: "import-history" */ "./views/ContactsCSVHistory.vue"
        ),
      meta: { requiresAuth: true }
    },

    {
      path: "/contacts/:id",
      name: "contact-details",
      component: () =>
        import(
          /* webpackChunkName: "contact-details" */ "./views/ContactsDetails.vue"
        ),
      meta: { requiresAuth: true }
    },

    // account
    {
      path: "/account/organization/general",
      name: "account-organization-general",
      component: () =>
        import(
          /* webpackChunkName: "account-organization-general" */ "./views/AccountOrganizationGeneral.vue"
        ),
      meta: { requiresAuth: true }
    },
    {
      path: "/account/organization/address",
      name: "account-organization-address",
      component: () =>
        import(
          /* webpackChunkName: "account-organization-address" */ "./views/AccountOrganizationAddress.vue"
        ),
      meta: { requiresAuth: true }
    },
    {
      path: "/account/organization/billing",
      name: "account-organization-billing",
      component: () =>
        import(
          /* webpackChunkName: "account-organization-billing" */ "./views/AccountOrganizationBilling.vue"
        ),
      meta: { requiresAuth: true }
    },
    {
      path: "/account/organization/billing-settings",
      name: "account-organization-billing-settings",
      component: () =>
        import(
          /* webpackChunkName: "account-organization-billing-settings" */ "./components/AccountOrganizationBillingSettings.vue"
        ),
      meta: { requiresAuth: true }
    },
    {
      path: "/account/integrations",
      name: "account-integrations",
      component: () =>
        import(
          /* webpackChunkName: "account-integrations" */ "./views/AccountIntegrations.vue"
        ),
      meta: { requiresAuth: true }
    },
    {
      path: "/account/preferences",
      name: "account-preferences",
      component: () =>
        import(
          /* webpackChunkName: "account-preferences" */ "./views/AccountPreferences.vue"
        ),
      meta: { requiresAuth: true }
    },
    {
      path: "/account/notifications",
      name: "account-notifications",
      component: () =>
        import(
          /* webpackChunkName: "account-integrations" */ "./views/AccountNotifications.vue"
        ),
      meta: { requiresAuth: true }
    },
    {
      path: "/account/address-validation",
      name: "account-address-validation",
      component: () =>
        import(
          /* webpackChunkName: "account-password" */ "./views/AccountAddressValidation.vue"
        ),
      meta: { requiresAuth: true }
    },
    {
      path: "/history/postcards",
      name: "postcards-history",
      component: () =>
        import(
          /* webpackChunkName: "postcards-history" */ "./views/PostcardsHistory.vue"
        ),
      meta: { requiresAuth: true }
    },

    ///letters
    {
      path: "/history/letters",
      name: "letters-history",
      component: () =>
        import(
          /* webpackChunkName: "letters-history" */ "./views/LettersHistory.vue"
        ),
      meta: { requiresAuth: true }
    },
    {
      path: "/letters-templates",
      name: "letters-templates",
      component: () =>
        import(
          /* webpackChunkName: "letters-templates" */ "./views/LettersTemplates.vue"
        ),
      meta: { requiresAuth: true }
    },
    {
      path: "/letters-templates/settings/:id",
      name: "letters-templates-settings",
      component: () =>
        import(
          /* webpackChunkName: "builder" */ "./views/LettersTemplatesSettings"
        ),
      meta: { requiresAuth: true }
    },

    {
      path: "/dashboard/mail",
      name: "dashboard-mail",
      component: () =>
        import(
          /* webpackChunkName: "dashboard-mail" */ "./views/DashboardMail.vue"
        ),
      meta: { requiresAuth: true }
    },
    {
      path: "/dashboard",
      name: "dashboard",
      redirect: "/dashboard/mail"
    },
    {
      path: "/account/advanced",
      name: "account-advanced",
      component: () =>
        import(
          /* webpackChunkName: "account-settings-advanced" */ "./views/AccountAddressValidation.vue"
        ),
      meta: { requiresAuth: true }
    },
    {
      path: "/",
      name: "home",
      redirect: "/dashboard"
    }
  ]
});

router.beforeEach((to, from, next) => {
  const { auth, account: accountStore } = store.state;

  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!auth.isAuthenticated && accountStore.identityLoaded) {
      next({
        path: "/login",
        query: {
          redirect: to.fullPath
        }
      });
    } else {
      next();
    }
  } else {
    next();
  }

  // redirect to letters or postrards
  // depends on user choose before
  // in templates list, history or contact details
  if (localStorage.getItem("mailpieceOption") === "letters") {
    if (to.path === "/templates") {
      next({ path: "/letters-templates" });
    }

    if (to.path === "/history/postcards") {
      next({ path: "/history/letters" });
    }
  }

  if (to.path === "/logout") {
    next();
  } else {
    // check for active subscription otherwise block
    if (
      router.history.current.path !== "/payment" &&
      accountStore.identity &&
      !accountStore.identity.account.active_payment
    ) {
      let redirectPath;

      if (!accountStore.identity.account.information_filled) {
        redirectPath = "/signup";
      } else {
        redirectPath = "/payment";
      }

      next({
        path: redirectPath
      });
    }
  }
});

export default router;
